/* Custom Playmat Designer — pmt- namespace.
 * ---------------------------------------------------------------------------
 * Third designer tool. The proven Metro plumbing (option proxying, artwork
 * gate, buy-button adoption, variant watching) is ported from the deck box
 * tool; everything visual is new and deliberately lighter: the preview is
 * CSS 2.5D driven by two canvases — NO three.js on this template at all.
 *
 * SELF-CONTAINED CART (2026-10-20): the tool now owns its /cart/add form and
 * resolves the variant id from its own option state and the variants table.
 * It no longer clicks or reads the theme's hidden pickers, and it never
 * adopts Metro buy buttons. Why: the theme's quick-view drawer renders a
 * second product form for the same product, and the old adoption logic could
 * end up submitting THAT form — whose variant id never follows the
 * customer's picks. Caught live on 2026-10-20: zone + stitched chosen, tool
 * showed the right price, the cart received the base variant at base price.
 * The adopted express-checkout buttons had the same fault, so the tool now
 * renders its own Add to cart; checkout is reached from the cart page.
 *
 * TWO-SIDED MODE (2026-10-20, for the 24x24 Custom Double Mat): the section
 * setting double_sided splits the mat into two stacked halves, one artwork
 * each. The TOP half is drawn rotated 180° — art and playzone overlay both —
 * so each player sees their side upright, the standard 2-player layout. The
 * playzone overlay (authored 24x14) is stretched into each 24x12 half and
 * mirrored. Art state lives in this.arts[side]; with the flag off there is
 * exactly one side covering the whole mat and behaviour is unchanged.
 * Orders in two-sided mode carry Artwork Side 1 / Artwork Side 2 (or a
 * same-art note), one composite Design Preview, and the _design note.
 *
 * Metro traps this file already knows about (hard-won on the other tools):
 * - product-info.js ignores option changes whose event target is outside
 *   <product-info>, so the tool's chips CLICK the real (hidden) inputs.
 * - Metro re-renders ProductBuyButtons' innerHTML on every variant change,
 *   destroying injected inputs — ensureFormInputs re-runs after each change.
 * - form.id on a product form returns the <input name="id"> element, not the
 *   id attribute. Always getAttribute('id').
 * - shopify:section:load fires on EVERY editor tweak — boot() guards re-init.
 */
(function () {
  'use strict';

  var STACK_BREAKPOINT = 899;   // must match the media query in the CSS

  function boot() {
    var roots = document.querySelectorAll('[data-playmat-designer]');
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__pmtInit) continue;
      roots[i].__pmtInit = true;
      try {
        new PlaymatDesigner(roots[i]);
      } catch (err) {
        roots[i].__pmtInit = false;
        console.error('[playmat-designer] init failed', err);
      }
    }
  }

  // ---------------------------------------------------------------- helpers

  function parseJSONAttr(el, name, fallback) {
    var raw = el.getAttribute(name);
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch (e) { return fallback; }
  }

  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  // Scale factor mapping image pixels -> canvas pixels such that the ROTATED
  // image still covers the whole canvas (same maths as the deck box tool).
  function coverFactor(iw, ih, cw, ch, rot) {
    var a = Math.abs(Math.cos(rot));
    var b = Math.abs(Math.sin(rot));
    var bboxW = iw * a + ih * b;
    var bboxH = iw * b + ih * a;
    return Math.max(cw / bboxW, ch / bboxH);
  }

  function bytesToLabel(b) {
    if (b >= 1024 * 1024) return (b / (1024 * 1024)).toFixed(1) + ' MB';
    if (b >= 1024) return Math.round(b / 1024) + ' KB';
    return b + ' B';
  }

  function roundedRectPath(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  // ------------------------------------------------------------ constructor

  function PlaymatDesigner(root) {
    this.root = root;
    root.__pmt = this;

    this.matWIn = parseFloat(root.getAttribute('data-mat-w-in'));
    if (isNaN(this.matWIn)) this.matWIn = 24;
    this.matHIn = parseFloat(root.getAttribute('data-mat-h-in'));
    if (isNaN(this.matHIn)) this.matHIn = 14;
    this.safeIn = parseFloat(root.getAttribute('data-safe-in'));
    if (isNaN(this.safeIn)) this.safeIn = 0.5;
    this.ppiGreat = parseFloat(root.getAttribute('data-ppi-great')) || 150;
    this.ppiMin = parseFloat(root.getAttribute('data-ppi-min')) || 100;
    this.maxBytes = (parseFloat(root.getAttribute('data-max-mb')) || 19) * 1024 * 1024;
    this.artSat = parseFloat(root.getAttribute('data-art-sat'));
    if (isNaN(this.artSat)) this.artSat = 1.25;

    this.optionNames = parseJSONAttr(root, 'data-option-names', []);
    this.zoneOptionName = root.getAttribute('data-zone-option') || 'Add Playzones';
    this.finishOptionName = root.getAttribute('data-finish-option') || 'Add Stitched Edging & Playmat thickness';
    this.tubeOptionName = root.getAttribute('data-tube-option') || 'Add a Playmat Tube';
    this.defaultFinish = root.getAttribute('data-default-finish') || '';
    this.defaultTube = root.getAttribute('data-default-tube') || '';

    this.twoSided = root.getAttribute('data-two-sided') === 'true';
    this.sides = this.twoSided ? 2 : 1;
    this.sideLabels = [
      root.getAttribute('data-side-1-label') || 'Side 1 (bottom)',
      root.getAttribute('data-side-2-label') || 'Side 2 (top)'
    ];

    this.requireArtwork = root.getAttribute('data-require-artwork') === 'true';
    this.hideOriginal = root.getAttribute('data-hide-original') === 'true';
    this.adoptBuy = root.getAttribute('data-adopt-buy') === 'true';
    this.movePaymentTerms = root.getAttribute('data-move-terms') === 'true';
    this.moneySample = root.getAttribute('data-money-sample') || '$1.00';
    this.safeNote = root.getAttribute('data-safe-note') || '';
    this.labels = {
      zoneGroup: root.getAttribute('data-zone-label') || 'Playzone overlay',
      zoneHint: root.getAttribute('data-zone-hint') || '',
      edgeGroup: root.getAttribute('data-edge-label') || 'Edge finish',
      edgeNone: root.getAttribute('data-edge-none') || 'No stitching',
      edgeStitch: root.getAttribute('data-edge-stitch') || 'Stitched edge',
      thickGroup: root.getAttribute('data-thick-label') || 'Thickness',
      thick2: root.getAttribute('data-thick-2') || 'Standard · 2 mm',
      thick3: root.getAttribute('data-thick-3') || 'Thick · 3 mm',
      tubeGroup: root.getAttribute('data-tube-label') || 'Playmat tube',
      zoneColor: root.getAttribute('data-zone-color-label') || 'Line colour'
    };
    this.showTubeDelta = root.getAttribute('data-tube-delta') !== 'false';

    this.zoneMap = parseJSONAttr(root, 'data-zone-map', {});
    this.zoneImages = {};        // value -> HTMLImageElement (lazy)

    this.variants = null;
    var vnode = root.querySelector('[data-pmt-variants-json]');
    if (vnode) { try { this.variants = JSON.parse(vnode.textContent); } catch (e) { this.variants = []; } }
    this.variants = this.variants || [];

    this.cartUrl = root.getAttribute('data-cart-url') || '/cart/add';
    this.cartReturn = root.getAttribute('data-cart-return') || '/cart';
    this.atcLabel = root.getAttribute('data-atc-label') || 'Add to cart';

    // The tool's own option state: option name -> selected value. Seeded from
    // the variants table (a ?variant= deep link wins, then the configured
    // defaults, then the first variant), and changed ONLY by this tool's own
    // controls — never by theme pickers or pub/sub events.
    this.sel = {};
    this.initSelection();

    // Per-side art. Single-sided mats have one entry covering the whole mat;
    // two-sided mats have one per half. tx/ty are inches from the SIDE's
    // centre (= mat centre when single-sided).
    function mkArt() {
      return { img: null, file: null, srcScale: 1, tx: 0, ty: 0, scale: 1, rot: 0, ppi: null };
    }
    this.arts = [];
    for (var s = 0; s < this.sides; s++) this.arts.push(mkArt());
    this.active = 0;        // which side the sliders / drag act on
    // Two-sided artwork layout: 'split' (one artwork per half), 'same'
    // (side 2 duplicates side 1, rotated with its half), or 'full' (ONE
    // continuous image across the whole mat — not rotated anywhere; the
    // playzone overlays stay mirrored per player regardless).
    // Default on the two-sided mat: ONE art across the whole mat.
    this.artMode = this.sides > 1 ? 'full' : 'split';

    this.state = {
      zone: null,     // real option values, read from the hidden picker
      finish: null,
      tube: null,
      zoneColor: '#ffffff'   // playzone line colour — white is what ships by default
    };
    this.viewMode = 'table';
    this.pointers = {};
    this.dragging = false;

    this.cacheDom();
    this.bindUI();
    this.watchVariant();

    // Core first, each optional enhancement isolated: a throw in any one of
    // these must never take out selection, pricing or the cart.
    var self = this;
    function step(name, fn) {
      try { fn.call(self); }
      catch (err) { console.error('[playmat-designer] ' + name + ' failed (continuing)', err); }
    }
    step('arrangeTwoSided', this.arrangeTwoSided);
    step('syncSideUI', this.syncSideUI);
    step('syncFromVariantPicker', this.syncFromVariantPicker);
    step('syncTabs', this.syncTabs);
    step('hideOriginalPicker', this.hideOriginalPicker);
    step('adoptBuyButtons', this.adoptBuyButtons);
    step('buildOptionProxy', this.buildOptionProxy);
    step('updatePrice', this.updatePrice);
    step('ensureFormInputs', this.ensureFormInputs);
    step('syncFormVariant', this.syncFormVariant);
    step('wireAtc', this.wireAtc);
    step('updateGate', this.updateGate);
    step('bindBuyGuard', this.bindBuyGuard);
    step('adoptPaymentTerms', this.adoptPaymentTerms);
    step('alignPriceToButtons', this.alignPriceToButtons);
    setTimeout(function () { self.alignPriceToButtons(); }, 800);
    setTimeout(function () { self.alignPriceToButtons(); }, 2500);

    this.render2D();
    this.updatePreviewState();
  }

  PlaymatDesigner.prototype.cacheDom = function () {
    var r = this.root;
    var q = function (sel) { return r.querySelector(sel); };
    this.el = {
      view: q('[data-pmt-view]'),
      canvasWrap: q('.pmt-canvas-wrap'),
      fileInput: q('[data-pmt-file]'),
      dropzone: q('[data-pmt-dropzone]'),
      replace: q('[data-pmt-replace]'),
      remove: q('[data-pmt-remove]'),
      filename: q('[data-pmt-filename]'),
      dpi: q('[data-pmt-dpi]'),
      zoom: q('[data-pmt-zoom]'),
      rotate: q('[data-pmt-rotate]'),
      status: q('[data-pmt-status]'),
      options: q('[data-pmt-options]'),
      stage: q('[data-pmt-stage]'),
      mat: q('[data-pmt-mat]'),
      matCanvas: q('[data-pmt-mat-canvas]'),
      badgeEdge: q('[data-pmt-badge-edge]'),
      badgeThick: q('[data-pmt-badge-thick]'),
      viewBtns: r.querySelectorAll('[data-pmt-viewmode]'),
      tabs: r.querySelectorAll('[data-pmt-tab]'),
      panes: r.querySelectorAll('[data-pmt-pane]'),
      priceNow: q('[data-pmt-price-now]'),
      priceWas: q('[data-pmt-price-was]'),
      priceNote: q('[data-pmt-price-note]'),
      atc: q('[data-pmt-atc]'),
      buySlot: q('[data-pmt-buy-slot]'),
      sideBtns: r.querySelectorAll('[data-pmt-side]'),
      sideNames: [q('[data-pmt-sidename-0]'), q('[data-pmt-sidename-1]')],
      sameArt: q('[data-pmt-sameart]'),
      modeBtns: r.querySelectorAll('[data-pmt-artmode]'),
      layoutSel: q('[data-pmt-layout-select]'),
      sideRow: q('[data-pmt-siderow]')
    };
  };

  /** One continuous artwork across the whole two-sided mat? */
  PlaymatDesigner.prototype.isFull = function () {
    return this.twoSided && this.artMode === 'full';
  };

  /**
   * Two-sided layout rearrangement (mockup 2026-10-21): the whole control
   * cluster — layout chips, side chips, the PPI pill and a compact strip of
   * Replace / Remove / Zoom / Rotate — moves ABOVE the pane title, with the
   * canvas after it. The long filename line is retired (the side chips carry
   * the names). Done by MOVING the existing nodes, so every bound listener
   * survives; the single-sided playmat keeps its original order.
   */
  PlaymatDesigner.prototype.arrangeTwoSided = function () {
    if (!this.twoSided) return;
    var card = this.root.querySelector('[data-pmt-pane="design"] .pmt-card');
    if (!card) return;
    var title = card.querySelector('.pmt-pane-title');
    var modes = card.querySelector('.pmt-modes');
    var sides = card.querySelector('[data-pmt-siderow]');
    var controls = card.querySelector('.pmt-controls');
    if (!title) return;

    var strip = document.createElement('div');
    strip.className = 'pmt-strip';
    if (this.el.dpi) strip.appendChild(this.el.dpi);
    var btns = document.createElement('div');
    btns.className = 'pmt-strip-row';
    if (this.el.replace) btns.appendChild(this.el.replace);
    if (this.el.remove) btns.appendChild(this.el.remove);
    if (controls) {
      var rows = controls.querySelectorAll('.pmt-row');
      for (var i = 0; i < rows.length; i++) btns.appendChild(rows[i]);
      controls.style.display = 'none';
    }
    strip.appendChild(btns);

    // Order: layout dropdown, side chips, Replace/Remove/Zoom/Rotate strip,
    // then the "Upload your art" title right above the upload window.
    if (modes) card.insertBefore(modes, title);
    if (sides) card.insertBefore(sides, title);
    card.insertBefore(strip, title);
  };

  // --------------------------------------------------------- per-side state

  /** The art record the controls act on (side 2 may mirror side 1). */
  PlaymatDesigner.prototype.art = function (i) {
    if (this.isFull()) return this.arts[0];
    var idx = i == null ? this.active : i;
    if (this.sameArt && idx === 1) return this.arts[0];
    return this.arts[idx];
  };

  /** Has this side got printable art? (side 2 is satisfied by Same art.) */
  PlaymatDesigner.prototype.sideFilled = function (i) {
    if (this.isFull()) return !!this.arts[0].img;
    if (this.sameArt && i === 1) return !!this.arts[0].img;
    return !!(this.arts[i] && this.arts[i].img);
  };

  PlaymatDesigner.prototype.hasAllArt = function () {
    for (var i = 0; i < this.sides; i++) {
      if (!this.sideFilled(i)) return false;
    }
    return true;
  };

  PlaymatDesigner.prototype.hasAnyArt = function () {
    for (var i = 0; i < this.sides; i++) {
      if (this.sideFilled(i)) return true;
    }
    return false;
  };

  /** First side still waiting for art, or -1 when the mat is covered. */
  PlaymatDesigner.prototype.firstEmptySide = function () {
    for (var i = 0; i < this.sides; i++) {
      if (!this.sideFilled(i)) return i;
    }
    return -1;
  };

  PlaymatDesigner.prototype.setActiveSide = function (i) {
    i = clamp(i, 0, this.sides - 1);
    if (this.active === i) { this.syncSideUI(); return; }
    this.active = i;
    var a = this.art();
    if (this.el.zoom) this.el.zoom.value = String(Math.round(a.scale * 100));
    if (this.el.rotate) this.el.rotate.value = String(Math.round(a.rot * 180 / Math.PI));
    this.syncSideUI();
    this.render2D();
  };

  /** Side chips, layout chips, per-side filenames, dropzone position. */
  PlaymatDesigner.prototype.syncSideUI = function () {
    var i;
    if (this.el.modeBtns) {
      for (i = 0; i < this.el.modeBtns.length; i++) {
        var mb = this.el.modeBtns[i];
        var mon = mb.getAttribute('data-pmt-artmode') === this.artMode;
        mb.classList.toggle('pmt-chip--on', mon);
        mb.setAttribute('aria-pressed', mon ? 'true' : 'false');
      }
    }
    if (this.el.layoutSel && this.el.layoutSel.value !== this.artMode) {
      this.el.layoutSel.value = this.artMode === 'full' ? 'full' : 'split';
    }
    // Side chips only mean something when each half has its own artwork.
    if (this.el.sideRow) this.el.sideRow.hidden = this.artMode !== 'split';
    if (this.el.sideBtns) {
      for (i = 0; i < this.el.sideBtns.length; i++) {
        var b = this.el.sideBtns[i];
        var idx = parseInt(b.getAttribute('data-pmt-side'), 10);
        var on = idx === this.active;
        b.classList.toggle('pmt-chip--on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.classList.toggle('pmt-side--filled', this.sideFilled(idx));
      }
    }
    for (i = 0; i < this.sides; i++) {
      var nameEl = this.el.sideNames && this.el.sideNames[i];
      if (nameEl) {
        var rec = this.sameArt && i === 1 ? this.arts[0] : this.arts[i];
        nameEl.textContent = this.sameArt && i === 1 && rec.file
          ? 'same as ' + this.sideLabels[0]
          : (rec.file ? rec.file.name : '');
      }
    }
    if (this.el.sameArt && this.el.sameArt.checked !== this.sameArt) {
      this.el.sameArt.checked = this.sameArt;
    }
    this.positionDropzone();
    if (this.el.filename) {
      var parts = [];
      if (this.isFull()) {
        if (this.arts[0].file) parts.push(this.arts[0].file.name + ' — ' + bytesToLabel(this.arts[0].file.size) + ' (whole mat)');
      } else {
        for (i = 0; i < this.sides; i++) {
          var r2 = this.arts[i];
          if (this.sameArt && i === 1) { if (this.arts[0].file) parts.push(this.sideLabels[1] + ': same art'); continue; }
          if (r2.file) parts.push((this.sides > 1 ? this.sideLabels[i] + ': ' : '') + r2.file.name + ' — ' + bytesToLabel(r2.file.size));
        }
      }
      this.el.filename.textContent = parts.join('  ·  ');
    }
  };

  /**
   * Two-sided: the upload overlay shrinks to cover only the half that still
   * needs art (bottom half = side 1, top half = side 2), so the filled half
   * stays visible and draggable. Hidden once the whole mat is covered.
   */
  PlaymatDesigner.prototype.positionDropzone = function () {
    var dz = this.el.dropzone;
    if (!dz) return;
    var strongEl = dz.querySelector('strong');
    if (strongEl && !strongEl.getAttribute('data-pmt-upload-label')) {
      strongEl.setAttribute('data-pmt-upload-label', strongEl.textContent);
    }
    if (!this.twoSided || this.isFull()) {
      // Single artwork covers everything: the overlay spans the whole mat
      // and the label carries no side suffix.
      dz.style.top = ''; dz.style.bottom = ''; dz.style.height = '';
      if (strongEl) strongEl.textContent = strongEl.getAttribute('data-pmt-upload-label');
      return;
    }
    var empty = this.firstEmptySide();
    if (empty === -1) { dz.style.top = ''; dz.style.bottom = ''; dz.style.height = ''; return; }
    if (this.hasAnyArt()) {
      dz.style.height = '50%';
      if (empty === 0) { dz.style.top = 'auto'; dz.style.bottom = '0'; }
      else { dz.style.top = '0'; dz.style.bottom = 'auto'; }
    } else {
      dz.style.top = ''; dz.style.bottom = ''; dz.style.height = '';
    }
    if (strongEl) {
      strongEl.textContent = strongEl.getAttribute('data-pmt-upload-label') + ' — ' + this.sideLabels[empty];
    }
  };

  // ---------------------------------------------------- finish value parsing

  /**
   * The product stores edge + thickness as ONE option with four combined
   * values. The tool presents them as two independent chip groups and
   * recombines the pair into the matching real value. Parsing is by pattern,
   * not by exact string, so admin wording tweaks don't break the split.
   */
  PlaymatDesigner.prototype.parseFinish = function (value) {
    var v = String(value || '');
    return {
      stitched: !/no\s*stitch/i.test(v),
      thick: /3\s*mm|thick/i.test(v)
    };
  };

  PlaymatDesigner.prototype.finishValueFor = function (stitched, thick) {
    var groups = this.readRealOptions();
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].name !== this.finishOptionName) continue;
      for (var j = 0; j < groups[i].values.length; j++) {
        var f = this.parseFinish(groups[i].values[j].value);
        if (f.stitched === stitched && f.thick === thick) return groups[i].values[j].value;
      }
    }
    return null;
  };

  // ---------------------------------------------------------------- defaults

  /**
   * Mert's chosen defaults: No stitching, 2 mm, No tube. Shopify lands on the
   * product's first variant, which is none of those — so the tool selects them
   * once on load. A URL that pins a ?variant= wins: that customer followed a
   * link to a specific configuration and must see exactly that.
   */
  /**
   * Seeds this.sel. A ?variant= deep link wins — that customer followed a
   * link to a specific configuration and must see exactly that. Otherwise
   * the first variant's options, overridden by the configured defaults
   * (No stitching 2 mm, No tube) when those values really exist.
   */
  PlaymatDesigner.prototype.initSelection = function () {
    var names = this.optionNames || [];
    if (!names.length || !this.variants.length) return;
    var base = this.variants[0];
    var pinned = false;
    var m = window.location.search.match(/[?&]variant=(\d+)/);
    if (m) {
      var want = parseInt(m[1], 10);
      for (var i = 0; i < this.variants.length; i++) {
        if (this.variants[i].id === want) { base = this.variants[i]; pinned = true; break; }
      }
    }
    for (var j = 0; j < names.length; j++) this.sel[names[j]] = base.o[j];
    if (!pinned) {
      this.applyDefaultValue(this.finishOptionName, this.defaultFinish);
      this.applyDefaultValue(this.tubeOptionName, this.defaultTube);
    }
  };

  /** Sets a default only when that option and value genuinely exist. */
  PlaymatDesigner.prototype.applyDefaultValue = function (name, value) {
    if (!name || !value) return;
    var idx = (this.optionNames || []).indexOf(name);
    if (idx < 0) return;
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].o[idx] === value) { this.sel[name] = value; return; }
    }
  };

  // ------------------------------------------------------------- 2D editor

  PlaymatDesigner.prototype.zoneImage = function (value) {
    if (!value || !this.zoneMap || !this.zoneMap[value]) return null;
    var cached = this.zoneImages[value];
    if (cached) return cached.complete && cached.naturalWidth ? cached : null;
    var self = this;
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () { self.render2D(); };
    img.src = this.zoneMap[value];
    this.zoneImages[value] = img;
    return null;
  };

  /**
   * The overlay in the customer's chosen line colour. The zone assets are
   * pure white line art, so a source-in composite onto an offscreen canvas
   * recolours every mark at once. Cached per asset + colour; white returns
   * the image untouched.
   */
  PlaymatDesigner.prototype.tintedZone = function (img) {
    var color = String(this.state.zoneColor || '#ffffff').toLowerCase();
    if (color === '#ffffff' || color === '#fff' || color === 'white') return img;
    var key = img.src + '|' + color;
    if (this._tint && this._tint.key === key) return this._tint.canvas;
    try {
      var canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || 2400;
      canvas.height = img.naturalHeight || 1400;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      ctx.globalCompositeOperation = 'source-in';
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      this._tint = { key: key, canvas: canvas };
      return canvas;
    } catch (e) {
      return img;   // tainted or out of memory: white overlay beats none
    }
  };

  PlaymatDesigner.prototype.setZoneColor = function (color) {
    if (!color || this.state.zoneColor === color) return;
    this.state.zoneColor = color;
    this.render2D();
    this.updateNote();
    this.syncZoneColorInputs();
  };

  /**
   * One side's rectangle on a canvas. Side 0 is the BOTTOM half (nearest the
   * customer), side 1 the top. Single-sided: the whole canvas.
   */
  PlaymatDesigner.prototype.sideRect = function (i, cw, ch) {
    if (this.sides === 1) return { x: 0, y: 0, w: cw, h: ch };
    var hs = ch / 2;
    return { x: 0, y: i === 0 ? hs : 0, w: cw, h: hs };
  };

  /**
   * Everything that PRINTS, drawn onto any canvas at any scale: dark base,
   * then per side its artwork and the playzone overlay. The top side of a
   * two-sided mat is rotated 180° — art and overlay both — so that player
   * sees their half upright. Used by render2D and captureShot so the editor,
   * the 2.5D preview and the order snapshot can never disagree.
   */
  PlaymatDesigner.prototype.drawPrint = function (ctx, cw, ch) {
    ctx.fillStyle = '#10142c';
    ctx.fillRect(0, 0, cw, ch);

    var pxPerIn = cw / this.matWIn;
    var zimgRaw = this.zoneImage(this.state.zone);
    var zimg = zimgRaw ? this.tintedZone(zimgRaw) : null;
    var full = this.isFull();
    var i, rect, a;

    // ---- artwork: one continuous image over the whole mat (full mode,
    // never rotated), or one image per side (top side rotated 180°)
    var artPasses = full ? 1 : this.sides;
    for (i = 0; i < artPasses; i++) {
      rect = full ? { x: 0, y: 0, w: cw, h: ch } : this.sideRect(i, cw, ch);
      a = full ? this.arts[0] : (this.sameArt && i === 1 ? this.arts[0] : this.arts[i]);
      var flip = !full && this.twoSided && i === 1;

      ctx.save();
      ctx.beginPath();
      ctx.rect(rect.x, rect.y, rect.w, rect.h);
      ctx.clip();

      if (a.img) {
        var iw = a.img.naturalWidth, ih = a.img.naturalHeight;
        var k = coverFactor(iw, ih, rect.w, rect.h, a.rot) * a.scale;
        ctx.save();
        if (ctx.filter !== undefined && this.artSat !== 1) {
          ctx.filter = 'saturate(' + this.artSat + ')';
        }
        ctx.imageSmoothingQuality = 'high';
        ctx.translate(rect.x + rect.w / 2, rect.y + rect.h / 2);
        if (flip) ctx.rotate(Math.PI);
        ctx.translate(a.tx * pxPerIn, a.ty * pxPerIn);
        ctx.rotate(a.rot);
        ctx.drawImage(a.img, -iw * k / 2, -ih * k / 2, iw * k, ih * k);
        ctx.restore();

        // effective print resolution: source pixels per printed inch
        a.ppi = rect.w / (k * this.matWIn) * (a.srcScale || 1);
      } else {
        a.ppi = null;
      }
      ctx.restore();
    }

    // ---- playzone line art: ALWAYS one per half on a two-sided mat, the
    // top one rotated to face its player — whatever the artwork layout
    if (zimg) {
      for (i = 0; i < this.sides; i++) {
        rect = this.sideRect(i, cw, ch);
        var zflip = this.twoSided && i === 1;
        ctx.save();
        ctx.beginPath();
        ctx.rect(rect.x, rect.y, rect.w, rect.h);
        ctx.clip();
        ctx.translate(rect.x + rect.w / 2, rect.y + rect.h / 2);
        if (zflip) ctx.rotate(Math.PI);
        ctx.drawImage(zimg, -rect.w / 2, -rect.h / 2, rect.w, rect.h);
        ctx.restore();
      }
    }
    this.lastPPI = this.art().ppi;
  };

  /** Editor-only guides: a safe-area rect per side, plus the centre seam. */
  PlaymatDesigner.prototype.drawGuides = function (ctx, cw, ch, g) {
    var inset = this.safeIn * (cw / this.matWIn);
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.setLineDash([6 * g, 5 * g]);
    ctx.lineWidth = 1.2 * g;
    for (var i = 0; i < this.sides; i++) {
      var r = this.sideRect(i, cw, ch);
      roundedRectPath(ctx, r.x + inset, r.y + inset, r.w - 2 * inset, r.h - 2 * inset, 4 * g);
      ctx.stroke();
    }
    ctx.restore();
    if (this.twoSided) {
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.35)';
      ctx.setLineDash([]);
      ctx.lineWidth = 1 * g;
      ctx.beginPath();
      ctx.moveTo(0, ch / 2);
      ctx.lineTo(cw, ch / 2);
      ctx.stroke();
      ctx.restore();
    }
  };

  PlaymatDesigner.prototype.render2D = function () {
    var view = this.el.view;
    if (!view) return;
    var cssW = view.clientWidth || 480;
    var cssH = view.clientHeight || Math.round(cssW * this.matHIn / this.matWIn);
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var cw = Math.round(cssW * dpr);
    var ch = Math.round(cssH * dpr);
    if (view.width !== cw) view.width = cw;
    if (view.height !== ch) view.height = ch;

    var ctx = view.getContext('2d');
    ctx.clearRect(0, 0, cw, ch);

    // base + what prints (art and zone, per side)
    this.drawPrint(ctx, cw, ch);

    // The preview mirrors what PRINTS — art and zone only — so the copy is
    // taken now, before the editor-only guides go on.
    this.mirrorToPreview();

    // safe-area guides (print is full bleed; guidance, not a trim line)
    this.drawGuides(ctx, cw, ch, dpr);

    // The placement snapshot for the order refreshes after every visual
    // change (debounced) — captured EAGERLY, never at submit time, so the
    // order can never be held up waiting for it.
    this.scheduleShot();

    if (this.safeNote && this.hasAnyArt()) {
      var inset = this.safeIn * (cw / this.matWIn);
      ctx.save();
      var fs = 10 * dpr;
      ctx.font = '500 ' + fs + 'px system-ui, sans-serif';
      var pad = 6 * dpr;
      var tw = ctx.measureText(this.safeNote).width;
      var bx = cw - inset - tw - pad * 2 - 6 * dpr;
      var by = ch - inset - fs - pad * 2 - 4 * dpr;
      ctx.fillStyle = 'rgba(10,14,30,0.65)';
      roundedRectPath(ctx, bx, by, tw + pad * 2, fs + pad * 1.6, (fs + pad * 1.6) / 2);
      ctx.fill();
      ctx.fillStyle = '#dfe8f2';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.safeNote, bx + pad, by + (fs + pad * 1.6) / 2 + dpr * 0.5);
      ctx.restore();
    }

    this.updateReadouts();
  };

  /**
   * Placement snapshot: art, zone overlay AND the dashed safe-area guide —
   * exactly as the customer arranged it, attached to the order as
   * properties[Design Preview] beside the original file. The guides make it
   * self-evidently a reference image, not print art. The cart-page and
   * cart-drawer thumbnail patches already prefer a "Design Preview" property,
   * so cart lines pick this image up automatically.
   *
   * Rendered OFFSCREEN at a fixed 2400x1400 (100px per printed inch of the
   * 24x14in mat — the same space the zone SVGs are authored in), never from
   * the on-screen canvas: the editor canvas is sized to the customer's
   * screen, which made phone-placed orders ship a ~700px reference the print
   * team could not judge placement from.
   *
   * Captured eagerly on a 600ms debounce after each change. If capture ever
   * fails the order still goes through with the artwork alone — a submit path
   * must never wait on an async artifact (hard-won sleeve-tool lesson).
   */
  PlaymatDesigner.prototype.scheduleShot = function () {
    if (!this.hasAnyArt()) return;
    var self = this;
    clearTimeout(this._shotTimer);
    this._shotTimer = setTimeout(function () { self.captureShot(); }, 600);
  };

  PlaymatDesigner.prototype.captureShot = function () {
    var self = this;
    if (!this.hasAnyArt()) return;
    try {
      var c = document.createElement('canvas');
      if (!c.toBlob) return;
      var cw = 2400;
      var ch = Math.round(cw * this.matHIn / this.matWIn);
      c.width = cw;
      c.height = ch;
      var ctx = c.getContext('2d');

      // Same drawing as render2D, at print-reference scale: base, art,
      // tinted zone overlay (per side), safe-area guides. The on-screen hint
      // pill is deliberately left out — it is UI, not placement information.
      this.drawPrint(ctx, cw, ch);
      this.drawGuides(ctx, cw, ch, 2);  // guide weight matches a dpr-2 screen

      c.toBlob(function (blob) {
        if (!blob || !self.hasAnyArt()) return;
        self.shotFile = new File([blob], 'placement-preview.jpg', { type: 'image/jpeg' });
        var form = self.form || self.findForm();
        var input = form && form.querySelector('[data-pmt-prop="shot"]');
        if (input) self.setFileOnInput(input, self.shotFile);
      }, 'image/jpeg', 0.9);
    } catch (e) { /* best-effort only */ }
  };

  /** The preview mat shows exactly what the editor canvas shows. */
  PlaymatDesigner.prototype.mirrorToPreview = function () {
    var src = this.el.view, dst = this.el.matCanvas;
    if (!src || !dst) return;
    if (dst.width !== src.width) dst.width = src.width;
    if (dst.height !== src.height) dst.height = src.height;
    var ctx = dst.getContext('2d');
    ctx.clearRect(0, 0, dst.width, dst.height);
    ctx.drawImage(src, 0, 0);
  };

  PlaymatDesigner.prototype.updateReadouts = function () {
    var dpi = this.el.dpi;
    if (!dpi) return;
    var self = this;
    function describe(p) {
      return p >= self.ppiGreat ? 'great' : (p >= self.ppiMin ? 'good' : 'low — aim for ' + self.ppiGreat + '+');
    }
    var parts = [];
    var worst = null;
    var readSides = this.isFull() ? 1 : this.sides;
    for (var i = 0; i < readSides; i++) {
      var a = this.sameArt && i === 1 ? this.arts[0] : this.arts[i];
      if (!a.ppi) continue;
      var p = Math.round(a.ppi);
      if (worst === null || p < worst) worst = p;
      parts.push((readSides > 1 ? this.sideLabels[i] + ': ' : '') + p + ' PPI — ' + describe(p));
      if (this.sameArt && i === 0) { i++; }  // one line covers both halves
    }
    if (parts.length) {
      dpi.textContent = parts.join('  ·  ');
      dpi.classList.remove('pmt-mid', 'pmt-warn');
      if (worst < this.ppiMin) dpi.classList.add('pmt-warn');
      else if (worst < this.ppiGreat) dpi.classList.add('pmt-mid');
      dpi.hidden = false;
    } else {
      dpi.hidden = true;
    }
  };

  // ---------------------------------------------------------------- upload

  PlaymatDesigner.prototype.loadFile = function (file, side) {
    var self = this;
    if (!file) return;
    if (this.isFull()) side = 0;
    if (side == null) side = this.twoSided ? (this.firstEmptySide() !== -1 ? this.firstEmptySide() : this.active) : 0;
    if (!/^image\//.test(file.type)) {
      return this.say('That file is not an image. Please upload a JPG or PNG.', true);
    }
    if (file.size > this.maxBytes) {
      return this.say('That file is ' + bytesToLabel(file.size) + '. Please keep it under ' +
        bytesToLabel(this.maxBytes) + '.', true);
    }

    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      // Downscale oversized images for smooth canvas work; the PPI meter is
      // corrected by srcScale so it reports the ORIGINAL file's resolution.
      // The customer's original file is what goes to the cart, untouched.
      var MAXDIM = 2600;
      var big = Math.max(img.naturalWidth, img.naturalHeight);
      if (big > MAXDIM) {
        var r = MAXDIM / big;
        var c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * r);
        c.height = Math.round(img.naturalHeight * r);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        var small = new Image();
        small.onload = function () {
          URL.revokeObjectURL(url);
          self.adopt(small, file, 1 / r, side);
        };
        small.src = c.toDataURL('image/jpeg', 0.92);
      } else {
        self.adopt(img, file, 1, side);
      }
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      self.say('That image could not be read. Try re-saving it as a JPG or PNG.', true);
    };
    img.src = url;
  };

  PlaymatDesigner.prototype.adopt = function (img, file, srcScale, side) {
    if (side == null) side = 0;
    var a = this.arts[side];
    a.img = img;
    a.file = file;
    a.srcScale = srcScale || 1;
    a.tx = 0; a.ty = 0; a.scale = 1; a.rot = 0;
    this.active = side;
    if (this.el.zoom) this.el.zoom.value = '100';
    if (this.el.rotate) this.el.rotate.value = '0';
    this.root.classList.toggle('pmt--has-art', this.hasAllArt());
    this.root.classList.toggle('pmt--any-art', this.hasAnyArt());
    this.root.classList.toggle('pmt--no-art', !this.hasAllArt());
    this.say('');
    this.syncSideUI();
    this.render2D();
    this.ensureFormInputs();
    this.updateGate();
    this.updatePrice();
  };

  PlaymatDesigner.prototype.clearArt = function (side) {
    // No side given (the Remove button): clear the active side; with Same art
    // on, clearing side 1 clears the whole mat's art source.
    if (side == null) side = this.twoSided ? this.active : 0;
    var a = this.arts[side];
    a.img = null; a.file = null; a.srcScale = 1;
    a.rot = 0; a.scale = 1; a.tx = 0; a.ty = 0; a.ppi = null;
    if (side === 0 && this.artMode === 'same') { this.artMode = 'split'; this.sameArt = false; }
    this.shotFile = null;
    clearTimeout(this._shotTimer);
    if (this.el.zoom) this.el.zoom.value = '100';
    if (this.el.rotate) this.el.rotate.value = '0';
    this.root.classList.toggle('pmt--has-art', this.hasAllArt());
    this.root.classList.toggle('pmt--any-art', this.hasAnyArt());
    this.root.classList.toggle('pmt--no-art', !this.hasAllArt());
    if (this.el.fileInput) this.el.fileInput.value = '';
    this.syncSideUI();
    this.render2D();
    this.ensureFormInputs();
    this.updateGate();
    this.updatePrice();
  };

  /**
   * Two-sided artwork layout: 'split', 'same' or 'full'. Art records are
   * never thrown away on a switch — flipping between layouts and back keeps
   * every upload and placement.
   */
  PlaymatDesigner.prototype.setArtMode = function (mode) {
    if (!this.twoSided) return;
    if (mode !== 'split' && mode !== 'same' && mode !== 'full') mode = 'split';
    if (this.artMode === mode) return;
    this.artMode = mode;
    this.sameArt = mode === 'same';
    if (mode !== 'split') this.active = 0;
    var a = this.art();
    if (this.el.zoom) this.el.zoom.value = String(Math.round(a.scale * 100));
    if (this.el.rotate) this.el.rotate.value = String(Math.round(a.rot * 180 / Math.PI));
    this.root.classList.toggle('pmt--has-art', this.hasAllArt());
    this.root.classList.toggle('pmt--any-art', this.hasAnyArt());
    this.root.classList.toggle('pmt--no-art', !this.hasAllArt());
    this.syncSideUI();
    this.render2D();
    this.ensureFormInputs();
    this.updateGate();
    this.updateNote();
  };

  PlaymatDesigner.prototype.say = function (msg, isError) {
    if (!this.el.status) return;
    this.el.status.textContent = msg || '';
    this.el.status.classList.toggle('pmt-warn', !!isError);
    this.el.status.hidden = !msg;
  };

  // ------------------------------------------------------------ interaction

  PlaymatDesigner.prototype.bindUI = function () {
    var self = this;
    var el = this.el;

    if (el.fileInput) {
      el.fileInput.addEventListener('change', function () {
        self.loadFile(this.files && this.files[0]);
      });
    }
    if (el.replace) {
      el.replace.addEventListener('click', function () {
        if (el.fileInput) el.fileInput.click();
      });
    }
    if (el.remove) {
      el.remove.addEventListener('click', function () { self.clearArt(); });
    }

    // Zoom + Rotate sliders, same behaviour as the deck box tool. Zoom's floor
    // is 100%: the print runs edge to edge, so the art may never fall short of
    // covering the whole mat.
    if (el.zoom) {
      el.zoom.addEventListener('input', function () {
        self.art().scale = clamp(parseFloat(this.value) / 100, 1, 4);
        self.render2D();
        self.updateNote();
      });
    }
    if (el.rotate) {
      el.rotate.addEventListener('input', function () {
        self.art().rot = parseFloat(this.value) * Math.PI / 180;
        self.render2D();
        self.updateNote();
      });
    }

    // Two-sided extras: side chips, the Same-art toggle. The dropzone loads
    // into the half it is covering (loadFile's default = first empty side).
    if (el.sideBtns) {
      Array.prototype.forEach.call(el.sideBtns, function (btn) {
        btn.addEventListener('click', function () {
          self.setActiveSide(parseInt(btn.getAttribute('data-pmt-side'), 10) || 0);
        });
      });
    }
    if (el.sameArt) {
      el.sameArt.addEventListener('change', function () {
        self.setArtMode(el.sameArt.checked ? 'same' : 'split');
      });
    }
    if (el.layoutSel) {
      el.layoutSel.addEventListener('change', function () {
        self.setArtMode(el.layoutSel.value);
      });
    }
    if (el.modeBtns) {
      Array.prototype.forEach.call(el.modeBtns, function (btn) {
        btn.addEventListener('click', function () {
          self.setArtMode(btn.getAttribute('data-pmt-artmode'));
        });
      });
    }

    if (el.dropzone) {
      ['dragenter', 'dragover'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.add('pmt-drop-hot');
        });
      });
      ['dragleave', 'drop'].forEach(function (evt) {
        el.dropzone.addEventListener(evt, function (e) {
          e.preventDefault(); el.dropzone.classList.remove('pmt-drop-hot');
        });
      });
      el.dropzone.addEventListener('drop', function (e) {
        if (e.dataTransfer && e.dataTransfer.files) self.loadFile(e.dataTransfer.files[0]);
      });
    }

    // drag to reposition, wheel / pinch to zoom — no sliders needed
    if (el.view) {
      var last = null;
      // Which half of the canvas a pointer event falls on (two-sided only):
      // the top half is side 2, the bottom half side 1.
      function sideAt(e) {
        if (!self.twoSided || self.isFull()) return 0;
        var box = el.view.getBoundingClientRect();
        return (e.clientY - box.top) < box.height / 2 ? 1 : 0;
      }
      el.view.addEventListener('pointerdown', function (e) {
        var side = sideAt(e);
        if (self.twoSided && !self.isFull() && self.sideFilled(side)) self.setActiveSide(self.sameArt && side === 1 ? 0 : side);
        if (!self.art().img) return;
        el.view.setPointerCapture(e.pointerId);
        self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        last = { x: e.clientX, y: e.clientY };
        self.dragging = true;
      });
      el.view.addEventListener('pointermove', function (e) {
        if (!self.dragging || !self.art().img) return;
        self.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
        var art = self.art();
        var ids = Object.keys(self.pointers);
        if (ids.length >= 2) {
          var a = self.pointers[ids[0]], b = self.pointers[ids[1]];
          var d = Math.hypot(a.x - b.x, a.y - b.y);
          if (self.pinchBase) {
            art.scale = clamp(self.pinchScale * (d / self.pinchBase), 1, 4);
            if (el.zoom) el.zoom.value = String(Math.round(art.scale * 100));
          } else {
            self.pinchBase = d; self.pinchScale = art.scale;
          }
          self.render2D();
          return;
        }
        var inPerPx = self.matWIn / (el.view.clientWidth || 480);
        // The top side renders rotated 180°, so a screen drag maps to the
        // opposite art-space direction there — inverted to keep the art
        // following the finger.
        var dir = (self.twoSided && !self.isFull() && self.active === 1) ? -1 : 1;
        var halfH = (self.twoSided && !self.isFull()) ? self.matHIn / 2 : self.matHIn;
        art.tx = clamp(art.tx + dir * (e.clientX - last.x) * inPerPx, -self.matWIn, self.matWIn);
        art.ty = clamp(art.ty + dir * (e.clientY - last.y) * inPerPx, -halfH, halfH);
        last = { x: e.clientX, y: e.clientY };
        self.render2D();
      });
      ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (evt) {
        el.view.addEventListener(evt, function (e) {
          delete self.pointers[e.pointerId];
          if (!Object.keys(self.pointers).length) {
            self.dragging = false; self.pinchBase = null;
            self.updateNote();
          }
        });
      });
      el.view.addEventListener('wheel', function (e) {
        var art = self.art();
        if (!art.img) return;
        e.preventDefault();
        art.scale = clamp(art.scale * (e.deltaY < 0 ? 1.06 : 0.94), 1, 4);
        if (el.zoom) el.zoom.value = String(Math.round(art.scale * 100));
        self.render2D();
        self.updateNote();
      }, { passive: false });
      el.view.addEventListener('dblclick', function () {
        var art = self.art();
        if (!art.img) return;
        art.tx = 0; art.ty = 0; art.scale = 1; art.rot = 0;
        if (el.zoom) el.zoom.value = '100';
        if (el.rotate) el.rotate.value = '0';
        self.render2D();
        self.updateNote();
      });
    }

    if (el.viewBtns) {
      Array.prototype.forEach.call(el.viewBtns, function (btn) {
        btn.addEventListener('click', function () {
          self.setViewMode(btn.getAttribute('data-pmt-viewmode'));
        });
      });
    }

    for (var i = 0; i < el.tabs.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          self.setTab(btn.getAttribute('data-pmt-tab'));
        });
      })(el.tabs[i]);
    }

    var ro = window.ResizeObserver ? new ResizeObserver(function () {
      self.syncTabs();
      self.render2D();
    }) : null;
    if (ro) ro.observe(this.root);
    window.addEventListener('resize', function () { self.syncTabs(); self.alignPriceToButtons(); });
  };

  PlaymatDesigner.prototype.setViewMode = function (mode) {
    this.viewMode = mode === 'flat' ? 'flat' : 'table';
    if (this.el.stage) this.el.stage.classList.toggle('pmt--flat', this.viewMode === 'flat');
    if (this.el.viewBtns) {
      Array.prototype.forEach.call(this.el.viewBtns, function (btn) {
        var on = btn.getAttribute('data-pmt-viewmode') === (mode === 'flat' ? 'flat' : 'table');
        btn.classList.toggle('pmt-view--on', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }
  };

  // ------------------------------------------------------------------ tabs

  PlaymatDesigner.prototype.setTab = function (name) {
    var el = this.el, i;
    this.currentTab = name;
    for (i = 0; i < el.tabs.length; i++) {
      var t = el.tabs[i];
      var on = t.getAttribute('data-pmt-tab') === name;
      t.classList.toggle('pmt-tab--on', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    }
    for (i = 0; i < el.panes.length; i++) {
      var p = el.panes[i];
      p.hidden = p.getAttribute('data-pmt-pane') !== name;
    }
    this.render2D();
  };

  PlaymatDesigner.prototype.syncTabs = function () {
    var stacked = window.matchMedia
      ? window.matchMedia('(max-width: ' + STACK_BREAKPOINT + 'px)').matches
      : window.innerWidth <= STACK_BREAKPOINT;
    if (stacked) {
      if (!this.stacked) {
        this.stacked = true;
        this.setTab(this.currentTab || 'design');
      }
    } else if (this.stacked !== false) {
      this.stacked = false;
      for (var i = 0; i < this.el.panes.length; i++) this.el.panes[i].hidden = false;
      this.render2D();
    }
  };

  // --------------------------------------------- real option controls (Metro)

  /**
   * The tool's OWN product form. Never a theme form: the page can hold
   * several /cart/add forms for this very product (the quick-view drawer
   * renders one), and any of them may sit on a stale variant id. This one is
   * plain multipart HTML — the browser carries the artwork files natively,
   * return_to lands the customer on the cart page — and its variant id is
   * written by syncFormVariant from the tool's own selection, nothing else.
   */
  PlaymatDesigner.prototype.findForm = function () {
    // The adopted Metro form (express wallets live on it) when present —
    // its variant id is ours via syncFormVariant. Own form otherwise.
    if (this.adoptedForm && document.contains(this.adoptedForm)) return this.adoptedForm;
    if (this.ownForm && document.contains(this.ownForm)) return this.ownForm;
    var form = document.createElement('form');
    form.setAttribute('action', this.cartUrl);
    form.setAttribute('method', 'post');
    form.setAttribute('enctype', 'multipart/form-data');
    form.setAttribute('id', 'PlaymatDesignerForm-' + Math.random().toString(36).slice(2, 8));
    form.className = 'pmt-own-form';
    form.style.display = 'none';
    function hidden(name, value) {
      var inp = document.createElement('input');
      inp.type = 'hidden';
      inp.name = name;
      inp.value = value;
      form.appendChild(inp);
      return inp;
    }
    this.ownIdInput = hidden('id', '');
    hidden('quantity', '1');
    hidden('return_to', this.cartReturn);
    this.root.appendChild(form);
    this.ownForm = form;
    return form;
  };

  /** Keeps the active form's variant id on the tool's current selection. */
  PlaymatDesigner.prototype.syncFormVariant = function () {
    var form = this.findForm();
    if (!form) return;
    var v = this.currentVariant();
    var input = form.querySelector('[name="id"]');
    if (input) {
      input.value = v ? String(v.id) : '';
      input.disabled = false;   // the theme disables it in some renders
    }
  };

  PlaymatDesigner.prototype.productScope = function () {
    var form = this.findForm();
    var scope = form && form.closest
      ? form.closest('product-info, .main-product-view, #MainProduct')
      : null;
    if (!scope) {
      scope = document.querySelector('product-info') ||
              document.querySelector('.main-product-view, #MainProduct');
    }
    return scope || document;
  };

  /**
   * The option groups, built from the VARIANTS TABLE — the same Liquid data
   * that prices the tool — with selection from this.sel. The theme's own
   * pickers are never read: the page may carry duplicate pickers (the
   * quick-view drawer) whose state has nothing to do with what the customer
   * chose here. Shape is unchanged from the old picker-reading version so
   * every consumer (proxy build, finish split, currentVariant) works as is.
   */
  PlaymatDesigner.prototype.readRealOptions = function () {
    var names = this.optionNames || [];
    var out = [];
    for (var i = 0; i < names.length; i++) {
      var group = { name: names[i], values: [], selected: this.sel[names[i]] != null ? this.sel[names[i]] : null };
      var seen = {};
      for (var j = 0; j < this.variants.length; j++) {
        var value = this.variants[j].o[i];
        if (value == null || seen['@' + value]) continue;
        seen['@' + value] = true;
        group.values.push({ value: value, checked: value === group.selected, disabled: false });
      }
      if (group.values.length) out.push(group);
    }
    return out;
  };

  /** A choice made in this tool: update the selection and everything on it. */
  PlaymatDesigner.prototype.chooseOption = function (name, value) {
    var idx = (this.optionNames || []).indexOf(name);
    if (idx < 0 || this.sel[name] === value) return;
    var exists = false;
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].o[idx] === value) { exists = true; break; }
    }
    if (!exists) return;
    this.sel[name] = value;
    this.syncFromVariantPicker();
    this.ensureFormInputs();
    this.syncFormVariant();
    this.refreshOptionProxy();
    this.updatePrice();
    this.updateGate();
    this.updateNote();
  };

  PlaymatDesigner.prototype.syncFromVariantPicker = function () {
    var vals = {};
    var names = this.optionNames || [];
    for (var i = 0; i < names.length; i++) {
      if (this.sel[names[i]] != null) vals[names[i]] = this.sel[names[i]];
    }
    this.applyOptionValues(vals);
    return vals;
  };

  PlaymatDesigner.prototype.applyOptionValues = function (vals) {
    var changed = false;
    var zone = vals[this.zoneOptionName];
    var finish = vals[this.finishOptionName];
    var tube = vals[this.tubeOptionName];
    if (zone !== undefined && zone !== this.state.zone) { this.state.zone = zone; changed = true; }
    if (finish !== undefined && finish !== this.state.finish) { this.state.finish = finish; changed = true; }
    if (tube !== undefined && tube !== this.state.tube) { this.state.tube = tube; changed = true; }
    if (changed) {
      this.render2D();
      this.updatePreviewState();
      this.updateNote();
    }
    return changed;
  };

  /**
   * Deliberately inert. The tool's selection is its own: theme pub/sub and
   * picker change events on this page can come from the quick-view drawer's
   * duplicate picker for this very product, and letting them write into the
   * tool's state is exactly how the live page ended up carting the base
   * variant (2026-10-20). Everything the tool needs re-runs inside
   * chooseOption.
   */
  PlaymatDesigner.prototype.watchVariant = function () {};

  // ------------------------------------------------------------ option proxy

  /**
   * The three groups, in the approved layout: playzone dropdown (12 values is
   * too many for chips), then edge + thickness side by side (the split of the
   * combined option), then the tube chips with No tube presented first.
   */
  PlaymatDesigner.prototype.buildOptionProxy = function () {
    var host = this.el.options;
    if (!host) return;
    host.textContent = '';
    host.hidden = true;

    var groups = this.readRealOptions();
    if (!groups.length) return;
    var byName = {};
    groups.forEach(function (g) { byName[g.name] = g; });
    var self = this;

    // ---- playzone dropdown
    var zoneGroup = byName[this.zoneOptionName];
    if (zoneGroup) {
      var zrow = document.createElement('div');
      zrow.className = 'pmt-opt pmt-opt--zone';
      var zlab = document.createElement('span');
      zlab.className = 'pmt-opt-label';
      zlab.textContent = this.labels.zoneGroup;
      zrow.appendChild(zlab);
      var sel = document.createElement('select');
      sel.className = 'pmt-zone-select';
      sel.setAttribute('data-pmt-zone-select', '');
      sel.setAttribute('aria-label', this.labels.zoneGroup);
      zoneGroup.values.forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v.value;
        opt.textContent = v.value;
        if (v.checked) opt.selected = true;
        if (v.disabled) opt.disabled = true;
        sel.appendChild(opt);
      });
      sel.addEventListener('change', function () {
        self.chooseOption(self.zoneOptionName, sel.value);
      });
      zrow.appendChild(sel);
      zrow.appendChild(this.buildZoneColorRow());
      if (this.labels.zoneHint) {
        var hint = document.createElement('p');
        hint.className = 'pmt-hint';
        hint.setAttribute('data-pmt-zone-hint', '');
        hint.textContent = this.labels.zoneHint;
        zrow.appendChild(hint);
      }
      host.appendChild(zrow);
    }

    // ---- edge + thickness, one row
    var finishGroup = byName[this.finishOptionName];
    if (finishGroup) {
      // Only offer what the product actually sells: the double mat has no
      // 3 mm variants, so its thickness group collapses to a single (hidden)
      // choice instead of showing a dead chip.
      var combos = { stitch: false, none: false, t2: false, t3: false };
      for (var fi = 0; fi < finishGroup.values.length; fi++) {
        var pf = this.parseFinish(finishGroup.values[fi].value);
        combos[pf.stitched ? 'stitch' : 'none'] = true;
        combos[pf.thick ? 't3' : 't2'] = true;
      }
      var edgeChips = [];
      if (combos.none) edgeChips.push({ key: 'none', label: this.labels.edgeNone });
      if (combos.stitch) edgeChips.push({ key: 'stitch', label: this.labels.edgeStitch });
      var thickChips = [];
      if (combos.t2) thickChips.push({ key: '2', label: this.labels.thick2 });
      if (combos.t3) thickChips.push({ key: '3', label: this.labels.thick3 });

      var pair = document.createElement('div');
      pair.className = 'pmt-opt-pair';
      if (edgeChips.length > 1) pair.appendChild(this.chipGroup('edge', this.labels.edgeGroup, edgeChips));
      if (thickChips.length > 1) pair.appendChild(this.chipGroup('thick', this.labels.thickGroup, thickChips));
      if (pair.children.length) host.appendChild(pair);
    }

    // ---- tube, No tube first (Mert's ordering)
    var tubeGroup = byName[this.tubeOptionName];
    if (tubeGroup) {
      var values = tubeGroup.values.slice().sort(function (a, b) {
        var an = /no\s*tube/i.test(a.value) ? 0 : 1;
        var bn = /no\s*tube/i.test(b.value) ? 0 : 1;
        return an - bn;
      });
      var trow = document.createElement('div');
      trow.className = 'pmt-opt';
      var tlab = document.createElement('span');
      tlab.className = 'pmt-opt-label';
      tlab.textContent = this.labels.tubeGroup;
      trow.appendChild(tlab);
      var tul = document.createElement('div');
      tul.className = 'pmt-chips';
      values.forEach(function (v) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pmt-chip';
        btn.setAttribute('data-pmt-tube', v.value);
        var span = document.createElement('span');
        span.textContent = v.value;
        span.style.color = 'inherit';
        btn.appendChild(span);
        if (!/no\s*tube/i.test(v.value)) {
          var delta = document.createElement('span');
          delta.className = 'pmt-chip-delta';
          delta.setAttribute('data-pmt-tube-delta', '');
          btn.appendChild(delta);
        }
        if (v.disabled) btn.setAttribute('aria-disabled', 'true');
        btn.addEventListener('click', function () {
          if (btn.getAttribute('aria-disabled') === 'true') return;
          self.chooseOption(self.tubeOptionName, v.value);
        });
        tul.appendChild(btn);
      });
      trow.appendChild(tul);
      host.appendChild(trow);
    }

    host.hidden = false;
    this.refreshOptionProxy();
  };

  /**
   * The line-colour row under the zone dropdown: six presets plus a free
   * picker, so the zone lines can be matched to the artwork. Hidden unless a
   * zone with overlay art is selected (it means nothing on a clean mat).
   */
  PlaymatDesigner.prototype.buildZoneColorRow = function () {
    var self = this;
    var row = document.createElement('div');
    row.className = 'pmt-zone-colors';
    row.setAttribute('data-pmt-zone-colors', '');
    row.hidden = true;
    var lab = document.createElement('span');
    lab.className = 'pmt-zone-color-label';
    lab.textContent = this.labels.zoneColor;
    row.appendChild(lab);
    ['#ffffff', '#0e0e11', '#f5c542', '#e23d3d', '#3aa0ff', '#c168ff'].forEach(function (c) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pmt-swatch';
      btn.setAttribute('data-pmt-zone-color', c);
      btn.setAttribute('aria-label', self.labels.zoneColor + ' ' + c);
      btn.style.background = c;
      btn.addEventListener('click', function () { self.setZoneColor(c); });
      row.appendChild(btn);
    });
    var custom = document.createElement('label');
    custom.className = 'pmt-swatch pmt-swatch--custom';
    custom.title = this.labels.zoneColor;
    var input = document.createElement('input');
    input.type = 'color';
    input.value = this.state.zoneColor || '#ffffff';
    input.setAttribute('data-pmt-zone-color-input', '');
    input.setAttribute('aria-label', this.labels.zoneColor);
    input.addEventListener('input', function () { self.setZoneColor(input.value); });
    custom.appendChild(input);
    row.appendChild(custom);
    return row;
  };

  /** Mirrors state.zoneColor onto the swatches and the free picker. */
  PlaymatDesigner.prototype.syncZoneColorInputs = function () {
    var root = this.root;
    var color = String(this.state.zoneColor || '#ffffff').toLowerCase();
    var btns = root.querySelectorAll('[data-pmt-zone-color]');
    var preset = false;
    for (var i = 0; i < btns.length; i++) {
      var on = btns[i].getAttribute('data-pmt-zone-color').toLowerCase() === color;
      btns[i].classList.toggle('pmt-swatch--on', on);
      btns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) preset = true;
    }
    var input = root.querySelector('[data-pmt-zone-color-input]');
    if (input) {
      if (input.value.toLowerCase() !== color) input.value = color;
      var wrap = input.closest('.pmt-swatch--custom');
      if (wrap) wrap.classList.toggle('pmt-swatch--on', !preset);
    }
    var form = this.form || this.findForm();
    var prop = form && form.querySelector('[data-pmt-prop="zonecolor"]');
    if (prop) {
      var val = this.zoneColorProperty();
      prop.value = val;
      prop.disabled = !val;   // a disabled input never submits an empty property
    }
  };

  /** The order property: only meaningful with an overlay on the mat. */
  PlaymatDesigner.prototype.zoneColorProperty = function () {
    var z = this.state.zone;
    if (!z || /no\s*playzone/i.test(z)) return '';
    var c = String(this.state.zoneColor || '#ffffff').toLowerCase();
    return c === '#ffffff' ? '' : c;
  };

  PlaymatDesigner.prototype.chipGroup = function (kind, label, chips) {
    var self = this;
    var row = document.createElement('div');
    row.className = 'pmt-opt';
    var lab = document.createElement('span');
    lab.className = 'pmt-opt-label';
    lab.textContent = label;
    row.appendChild(lab);
    var ul = document.createElement('div');
    ul.className = 'pmt-chips';
    chips.forEach(function (c) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pmt-chip';
      btn.textContent = c.label;
      btn.setAttribute('data-pmt-' + kind, c.key);
      btn.addEventListener('click', function () {
        var f = self.parseFinish(self.state.finish);
        var stitched = kind === 'edge' ? c.key === 'stitch' : f.stitched;
        var thick = kind === 'thick' ? c.key === '3' : f.thick;
        var value = self.finishValueFor(stitched, thick);
        if (value) self.chooseOption(self.finishOptionName, value);
      });
      ul.appendChild(btn);
    });
    row.appendChild(ul);
    return row;
  };

  /** Mirrors the real picker's state back onto the proxy controls. */
  PlaymatDesigner.prototype.refreshOptionProxy = function () {
    var root = this.root;
    var groups = this.readRealOptions();
    var byName = {};
    groups.forEach(function (g) { byName[g.name] = g; });

    var zoneGroup = byName[this.zoneOptionName];
    var sel = root.querySelector('[data-pmt-zone-select]');
    if (sel && zoneGroup && zoneGroup.selected != null && sel.value !== zoneGroup.selected) {
      sel.value = zoneGroup.selected;
    }

    var f = this.parseFinish(this.state.finish);
    var mark = function (attr, key) {
      var btns = root.querySelectorAll('[' + attr + ']');
      for (var i = 0; i < btns.length; i++) {
        var on = btns[i].getAttribute(attr) === key;
        btns[i].classList.toggle('pmt-chip--on', on);
        btns[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    };
    if (this.state.finish != null) {
      mark('data-pmt-edge', f.stitched ? 'stitch' : 'none');
      mark('data-pmt-thick', f.thick ? '3' : '2');
    }
    if (this.state.tube != null) {
      mark('data-pmt-tube', this.state.tube);
    }

    this.updateTubeDelta();
    this.updateZoneHint();
    this.syncZoneColorInputs();
  };

  /** "+$20.50" on the tube chip: real price difference at the current combo. */
  PlaymatDesigner.prototype.updateTubeDelta = function () {
    var el = this.root.querySelector('[data-pmt-tube-delta]');
    if (!el) return;
    if (!this.showTubeDelta) { el.textContent = ''; return; }
    var names = this.optionNames || [];
    var zi = names.indexOf(this.zoneOptionName);
    var fi = names.indexOf(this.finishOptionName);
    var ti = names.indexOf(this.tubeOptionName);
    if (ti < 0) { el.textContent = ''; return; }
    var withT = null, without = null;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (zi >= 0 && this.state.zone != null && v.o[zi] !== this.state.zone) continue;
      if (fi >= 0 && this.state.finish != null && v.o[fi] !== this.state.finish) continue;
      if (/no\s*tube/i.test(v.o[ti])) { without = v; } else { withT = v; }
    }
    if (withT && without && withT.c > without.c) {
      el.textContent = ' +' + this.formatCents(withT.c - without.c);
    } else {
      el.textContent = '';
    }
  };

  /** The zone hint doubles as the "overlay art pending" notice. */
  PlaymatDesigner.prototype.updateZoneHint = function () {
    var z = this.state.zone;
    var noZone = !z || /no\s*playzone/i.test(z);
    var colors = this.root.querySelector('[data-pmt-zone-colors]');
    if (colors) colors.hidden = noZone || !(this.zoneMap && this.zoneMap[z]);
    var hint = this.root.querySelector('[data-pmt-zone-hint]');
    if (!hint) return;
    if (!noZone && (!this.zoneMap || !this.zoneMap[z])) {
      hint.textContent = z + ' prints on your mat — its preview overlay is being added, so the preview shows the plain artwork for now.';
    } else {
      hint.textContent = this.labels.zoneHint;
    }
  };

  /** Stage classes + badges reflect the selected finish. */
  PlaymatDesigner.prototype.updatePreviewState = function () {
    var f = this.parseFinish(this.state.finish);
    if (this.el.stage) {
      this.el.stage.classList.toggle('pmt--stitched', this.state.finish != null && f.stitched);
      this.el.stage.classList.toggle('pmt--thick', this.state.finish != null && f.thick);
      this.el.stage.classList.toggle('pmt--flat', this.viewMode === 'flat');
    }
    if (this.el.badgeEdge) {
      this.el.badgeEdge.textContent = this.state.finish == null ? '' :
        (f.stitched ? this.labels.edgeStitch : this.labels.edgeNone);
      this.el.badgeEdge.hidden = this.state.finish == null;
    }
    if (this.el.badgeThick) {
      this.el.badgeThick.textContent = this.state.finish == null ? '' : (f.thick ? '3 mm' : '2 mm');
      this.el.badgeThick.hidden = this.state.finish == null;
    }
  };

  PlaymatDesigner.prototype.hideOriginalPicker = function () {
    if (!this.hideOriginal) return;
    var scope = this.productScope();
    if (!scope || !scope.querySelectorAll) return;
    var nodes = scope.querySelectorAll('variant-selects, .product-block-variant-picker');
    for (var i = 0; i < nodes.length; i++) {
      if (!nodes[i].classList.contains('pmt-visually-hidden')) {
        nodes[i].classList.add('pmt-visually-hidden');
      }
    }
  };

  // ---------------------------------------------------------------- pricing

  PlaymatDesigner.prototype.moneyParts = function () {
    if (this._money) return this._money;
    var m = String(this.moneySample);
    var first = m.search(/[0-9]/);
    var last = m.length - 1 - String(m).split('').reverse().join('').search(/[0-9]/);
    this._money = {
      pre: first > 0 ? m.slice(0, first) : '',
      post: last < m.length - 1 ? m.slice(last + 1) : '',
      comma: /1[.,]00/.test(m) ? m.charAt(m.search(/1[.,]00/) + 1) : '.'
    };
    return this._money;
  };

  PlaymatDesigner.prototype.formatCents = function (cents) {
    var mp = this.moneyParts();
    var whole = Math.floor(cents / 100);
    var frac = String(Math.round(cents % 100));
    while (frac.length < 2) frac = '0' + frac;
    var s = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, mp.comma === ',' ? '.' : ',');
    return mp.pre + s + mp.comma + frac + mp.post;
  };

  PlaymatDesigner.prototype.currentVariant = function () {
    var names = this.optionNames || [];
    var groups = this.readRealOptions();
    var sel = {};
    groups.forEach(function (g) { if (g.selected != null) sel[g.name] = g.selected; });
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i], ok = true;
      for (var j = 0; j < names.length; j++) {
        if (sel[names[j]] !== undefined && v.o[j] !== sel[names[j]]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  PlaymatDesigner.prototype.updatePrice = function () {
    if (this.el.priceNow) {
      var v = this.currentVariant();
      this.el.priceNow.textContent = v ? v.p : '';
      if (this.el.priceWas) {
        var show = v && v.cp && v.cp > v.c;
        this.el.priceWas.textContent = show ? this.formatCents(v.cp) : '';
        this.el.priceWas.hidden = !show;
      }
    }
    if (this.el.priceNote) {
      var f = this.parseFinish(this.state.finish);
      var bits = [];
      if (this.state.zone) bits.push(this.state.zone);
      if (this.state.finish != null) {
        bits.push(f.stitched ? this.labels.edgeStitch : this.labels.edgeNone);
        bits.push(f.thick ? '3 mm' : '2 mm');
      }
      if (this.state.tube) bits.push(this.state.tube);
      this.el.priceNote.textContent = bits.join(' · ');
    }
    this.updateTubeDelta();
  };

  // ------------------------------------------------------------ add to cart

  PlaymatDesigner.prototype.wireAtc = function () {
    var btn = this.el.atc;
    if (!btn) return;
    var form = this.findForm();
    if (!form) return;

    // NOT form.id — the product form contains <input name="id">, which the
    // legacy named-property getter exposes as an own property shadowing the
    // real id accessor. Always getAttribute on a product form.
    var formId = form.getAttribute('id');
    if (!formId) {
      formId = 'PlaymatDesignerProductForm';
      form.setAttribute('id', formId);
    }
    btn.setAttribute('form', formId);

    if (btn.__pmtWired) return;
    btn.__pmtWired = true;
    btn.addEventListener('click', function () {
      if (btn.getAttribute('aria-disabled') === 'true') return;
      btn.classList.add('pmt-loading');
      setTimeout(function () { btn.classList.remove('pmt-loading'); }, 5000);
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { btn.classList.remove('pmt-loading'); });
        subscribe(PUB_SUB_EVENTS.cartError, function () { btn.classList.remove('pmt-loading'); });
      } catch (e) { /* not fatal */ }
    }
  };

  /**
   * Adopts Metro's buy-buttons block — Add to cart, the express wallets
   * (Shop Pay / Buy it now) and More payment options — into the buy bar.
   * On this theme that block belongs to the quick-view drawer, whose form
   * used to sit on a stale variant id (the 2026-10-20 wrong-variant bug).
   * Adoption is safe again because the designer now OWNS that form's state:
   * syncFormVariant writes the variant id from the tool's selection after
   * every change, ensureFormInputs re-injects the artwork properties, and a
   * MutationObserver re-asserts both whenever Metro re-renders the block.
   * The express buttons serialize their form at click time, so they carry
   * the synced id and the artwork properties. If no block exists to adopt,
   * the tool falls back to its own Add to cart + its own form.
   */
  PlaymatDesigner.prototype.adoptBuyButtons = function () {
    if (!this.adoptBuy) return;
    var slot = this.el.buySlot;
    if (!slot) return;

    var bb = slot.querySelector('[id^="ProductBuyButtons-"]');
    if (!bb) {
      bb = document.querySelector('.main-product-view [id^="ProductBuyButtons-"], product-info [id^="ProductBuyButtons-"]') ||
           document.querySelector('[id^="ProductBuyButtons-"]');
      if (bb) slot.appendChild(bb);
    }

    if (bb) {
      var form = slot.querySelector('form[action*="/cart/add"]');
      if (form && form.querySelector('[name="id"]')) {
        this.adoptedForm = form;
        var own = this.root.querySelector('.pmt-atc');
        if (own && own.parentNode) own.parentNode.removeChild(own);
        this.el.atc = null;
        this.pruneAdoptedExtras();
        this.watchAdoptedBlock(bb);
      }
    }

    if (!this.adoptedForm && !slot.querySelector('[data-pmt-atc]')) {
      var btn = document.createElement('button');
      btn.type = 'submit';
      btn.name = 'add';
      btn.className = 'pmt-atc';
      btn.setAttribute('data-pmt-atc', '');
      btn.textContent = this.atcLabel;
      slot.appendChild(btn);
      this.el.atc = btn;
    }
    this.root.classList.add('pmt--adopted-buy');
  };

  /**
   * The adopted block can carry the theme's own artwork-upload field (the
   * old "Artwork ready to go?" input on the double mat). The designer IS
   * the upload now, so that field is hidden and its input disabled — left
   * enabled it would post a stray properties[Artwork] beside the
   * designer's own files.
   */
  PlaymatDesigner.prototype.pruneAdoptedExtras = function () {
    var form = this.adoptedForm;
    if (!form) return;
    var files = form.querySelectorAll('input[type="file"]:not([data-pmt-prop])');
    for (var i = 0; i < files.length; i++) {
      var inp = files[i];
      inp.disabled = true;
      inp.removeAttribute('required');
      var wrap = inp.closest('.product-form-artwork-upload, .product-block') || inp.parentElement;
      if (wrap && wrap !== form) wrap.style.display = 'none';
    }
  };

  /** The Shop Pay installments banner, placed under the express buttons. */
  PlaymatDesigner.prototype.adoptPaymentTerms = function () {
    if (!this.movePaymentTerms || !this.adoptedForm) return;
    var slot = this.el.buySlot;
    if (!slot) return;

    var terms = this._paymentTerms ||
                document.querySelector('.product-block-payment-installments');
    if (!terms) return;
    this._paymentTerms = terms;

    if (this._termsPoll) { clearInterval(this._termsPoll); this._termsPoll = null; }
    if (!slot.contains(terms)) slot.appendChild(terms);

    function refine() {
      var payBtn = slot.querySelector('.shopify-payment-button');
      if (!payBtn) return false;
      var more = payBtn.querySelector('.shopify-payment-button__more-options');
      if (more) {
        if (terms.parentNode !== payBtn || terms.nextElementSibling !== more) {
          payBtn.insertBefore(terms, more);
        }
        return true;
      }
      if (terms.previousElementSibling !== payBtn) {
        payBtn.parentNode.insertBefore(terms, payBtn.nextSibling);
      }
      return false;
    }

    if (refine()) return;
    var self = this;
    var tries = 0;
    this._termsPoll = setInterval(function () {
      tries += 1;
      if (refine() || tries > 40) {
        clearInterval(self._termsPoll);
        self._termsPoll = null;
      }
    }, 250);
  };

  /**
   * Metro re-renders the buy-buttons innerHTML on its own events, which
   * destroys the injected property inputs and can rewrite the variant id.
   * Any mutation inside the adopted block re-asserts both (debounced).
   */
  PlaymatDesigner.prototype.watchAdoptedBlock = function (bb) {
    if (this._bbObserver || !window.MutationObserver) return;
    var self = this;
    this._bbObserver = new MutationObserver(function () {
      clearTimeout(self._bbTimer);
      self._bbTimer = setTimeout(function () {
        try {
          var slot = self.el.buySlot;
          var form = slot && slot.querySelector('form[action*="/cart/add"]');
          if (form && form !== self.adoptedForm && form.querySelector('[name="id"]')) {
            self.adoptedForm = form;   // Metro replaced the form wholesale
          }
          self.pruneAdoptedExtras();
          self.ensureFormInputs();
          self.syncFormVariant();
          self.updateGate();
        } catch (e) { /* never let an observer throw */ }
      }, 120);
    });
    this._bbObserver.observe(bb, { childList: true, subtree: true });
  };

  /**
   * Catches any buy attempt made before a design exists — bound on the buy
   * bar in capture phase, so it also covers express checkout iframes.
   */
  PlaymatDesigner.prototype.bindBuyGuard = function () {
    var buy = this.root.querySelector('.pmt-buy');
    if (!buy || buy.__pmtBuyGuard) return;
    buy.__pmtBuyGuard = true;
    var self = this;
    buy.addEventListener('click', function (e) {
      if (!self.requireArtwork || self.hasAllArt()) return;
      e.preventDefault();
      e.stopPropagation();
      self.say('Please upload your design first — both Add to cart and express checkout need it.', true);
      var zone = self.el.dropzone;
      if (zone) {
        zone.classList.remove('pmt-flash');
        void zone.offsetWidth;
        zone.classList.add('pmt-flash');
        setTimeout(function () { zone.classList.remove('pmt-flash'); }, 1400);
      }
      var target = self.el.canvasWrap || zone;
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, true);
  };

  PlaymatDesigner.prototype.alignPriceToButtons = function () {
    var price = this.root.querySelector('.pmt-price');
    var slot = this.el.buySlot;
    if (!price) return;
    var btn = (slot && slot.querySelector(
      '.addtocart-button, .shopify-payment-button__button, [name="add"]'));
    if (!btn || !slot) { price.style.marginTop = ''; return; }
    var offset = Math.round(btn.getBoundingClientRect().top -
                            slot.getBoundingClientRect().top);
    price.style.marginTop = (offset > 0 && offset < 200) ? offset + 'px' : '';
  };

  // ------------------------------------------------------------ cart handoff

  PlaymatDesigner.prototype.designNote = function () {
    var st = this.state;
    var parts = [];
    var full = this.isFull();
    parts.push((this.twoSided ? 'Double mat ' : 'Playmat ') + this.matWIn + 'x' + this.matHIn + 'in' +
      (this.twoSided
        ? (full
            ? ' (ONE artwork across the whole mat — do NOT rotate any half)'
            : ' (two sides, top half printed rotated 180deg)')
        : ''));
    if (st.zone) parts.push('zone: ' + st.zone + (this.twoSided ? ' (mirrored per side)' : ''));
    var zc = this.zoneColorProperty();
    if (zc) parts.push('zone colour ' + zc);
    if (st.finish) parts.push(st.finish);
    if (st.tube) parts.push(st.tube);
    // Shopify replaces the uploaded filename with a hash; the customer's own
    // name for each file is recorded so a missing file can be asked for by
    // name.
    function artLine(label, a) {
      return label + ': ' + a.file.name +
        ' | scale ' + a.scale.toFixed(2) +
        ' | offset ' + a.tx.toFixed(2) + 'in,' + a.ty.toFixed(2) + 'in' +
        ' | rotate ' + Math.round(a.rot * 180 / Math.PI) + 'deg' +
        (a.ppi ? ' | src ' + Math.round(a.ppi) + 'ppi' : '');
    }
    if (full) {
      if (this.arts[0].file) parts.push(artLine('Whole mat', this.arts[0]));
    } else {
      for (var i = 0; i < this.sides; i++) {
        var a = this.sameArt && i === 1 ? this.arts[0] : this.arts[i];
        if (!a.file) continue;
        if (this.sameArt && i === 1) { parts.push(this.sideLabels[1] + ': SAME ART as ' + this.sideLabels[0]); continue; }
        parts.push(artLine(this.sides > 1 ? this.sideLabels[i] : 'art', a));
      }
    }
    return parts.join(' | ');
  };

  PlaymatDesigner.prototype.updateNote = function () {
    var form = this.form || this.findForm();
    if (!form) return;
    var note = form.querySelector('[data-pmt-prop="note"]');
    if (!note) return;
    note.value = this.hasAnyArt() ? this.designNote() : '';
  };

  PlaymatDesigner.prototype.setFileOnInput = function (input, file) {
    if (!input) return;
    if (!file) { input.value = ''; return; }
    try {
      var dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
    } catch (e) {
      console.warn('[playmat-designer] could not attach file to form input', e);
    }
  };

  /**
   * Injects the line-item-property inputs into the product form. Re-run after
   * every variant change: Metro replaces the buy-buttons innerHTML, which
   * destroys anything injected inside it — these live on the form itself, but
   * the re-run also re-attaches the file after any form re-render.
   */
  PlaymatDesigner.prototype.ensureFormInputs = function () {
    var form = this.findForm();
    if (!form) return;
    this.form = form;

    var self = this;
    function ensure(key, type, name) {
      var found = form.querySelector('[data-pmt-prop="' + key + '"]');
      if (!found) {
        found = document.createElement('input');
        found.type = type;
        found.name = name;
        found.setAttribute('data-pmt-prop', key);
        found.className = 'pmt-visually-hidden';
        if (type === 'file') found.setAttribute('tabindex', '-1');
        form.appendChild(found);
      }
      return found;
    }

    if (this.twoSided) {
      // One artwork property per side, or a single whole-mat file. With
      // Same art on, side 2's input is left empty (and disabled) — the
      // _design note records the mirroring, so the print team never has to
      // guess which file covers which half.
      var full = this.isFull();
      var art0 = ensure('art0', 'file', 'properties[Artwork Side 1]');
      var art1 = ensure('art1', 'file', 'properties[Artwork Side 2]');
      var artF = ensure('artfull', 'file', 'properties[Artwork (Full Mat)]');
      this.setFileOnInput(art0, full ? null : this.arts[0].file);
      this.setFileOnInput(art1, full || this.sameArt ? null : this.arts[1].file);
      this.setFileOnInput(artF, full ? this.arts[0].file : null);
      art0.disabled = full || !this.arts[0].file;
      art1.disabled = full || this.sameArt || !this.arts[1].file;
      artF.disabled = !full || !this.arts[0].file;
    } else {
      var art = ensure('art', 'file', 'properties[Artwork]');
      this.setFileOnInput(art, this.arts[0].file);
    }
    var shot = ensure('shot', 'file', 'properties[Design Preview]');
    var note = ensure('note', 'hidden', 'properties[_design]');
    var zcol = ensure('zonecolor', 'hidden', 'properties[Playzone Line Colour]');

    this.setFileOnInput(shot, this.shotFile);
    note.value = this.hasAnyArt() ? this.designNote() : '';
    zcol.value = this.zoneColorProperty();
    zcol.disabled = !zcol.value;   // a disabled input never submits an empty property

    if (!form.__pmtGuard) {
      form.__pmtGuard = true;
      form.addEventListener('submit', function (e) {
        if (!self.requireArtwork) return;
        if (!self.hasAllArt()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.say(self.twoSided
            ? 'Please upload artwork for both sides before adding to cart.'
            : 'Please upload your design before adding to cart.', true);
          self.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, true);
    }
  };

  PlaymatDesigner.prototype.updateGate = function () {
    if (!this.requireArtwork) return;
    var form = this.form || this.findForm();
    if (!form) return;
    var ready = this.hasAllArt();
    var all = document.querySelectorAll('[name="add"]');
    var btns = [];
    for (var n = 0; n < all.length; n++) {
      if (all[n].form === form) btns.push(all[n]);
    }
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      if (ready) {
        if (b.getAttribute('data-pmt-blocked') === 'true') {
          b.removeAttribute('data-pmt-blocked');
          b.removeAttribute('aria-disabled');
          b.classList.remove('pmt-blocked');
        }
      } else {
        b.setAttribute('data-pmt-blocked', 'true');
        b.setAttribute('aria-disabled', 'true');
        b.classList.add('pmt-blocked');
      }
    }
    this.root.classList.toggle('pmt--ready', ready);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
